import { ShoppingCart, ArrowUpRight, Phone } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { openWhatsApp } from "../lib/whatsapp";
import { PHONE_DISPLAY } from "../data/products";

export const ProductCard = ({ product }) => {
  const { addItem, openCheckout } = useCart();
  const navigate = useNavigate();
  const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;

  const handleOrderNow = () => {
    if (hasVariants) {
      // Send to product detail so user can pick a variant first
      navigate(`/produs/${product.slug}`);
      return;
    }
    addItem(product, 1);
    openCheckout();
  };

  const handleWhatsApp = () => {
    openWhatsApp();
  };

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="group relative flex flex-col bg-white rounded-3xl border border-sky-100 overflow-hidden hover:border-cyan-200 hover:shadow-2xl hover:shadow-cyan-100/40 hover:-translate-y-1 transition-all duration-300"
    >
      <Link
        to={`/produs/${product.slug}`}
        data-testid={`product-image-link-${product.id}`}
        className="relative block aspect-square overflow-hidden bg-white"
      >
        {product.discount && (
          <div className="absolute top-4 left-4 z-10 bg-rose-500 text-white text-xs font-black px-2.5 py-1 rounded-full">
            -{product.discount}%
          </div>
        )}
        {product.badge && (
          <div className="absolute top-4 right-4 z-10 bg-white/95 backdrop-blur text-cyan-700 text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border border-cyan-100">
            {product.badge}
          </div>
        )}
        <img
          src={product.image}
          alt={product.title}
          referrerPolicy="no-referrer"
          loading="eager"
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
        />
      </Link>

      <div className="flex flex-col flex-1 p-5 md:p-6">
        <Link
          to={`/produs/${product.slug}`}
          data-testid={`product-title-${product.id}`}
          className="font-heading font-bold text-base md:text-lg leading-snug text-slate-900 hover:text-cyan-600 transition-colors line-clamp-2"
        >
          {product.title}
        </Link>
        <p className="mt-2 text-sm text-slate-500 line-clamp-2">{product.short}</p>

        {hasVariants && (
          <div
            data-testid={`variant-badge-${product.id}`}
            className="mt-2.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-50 text-cyan-700 text-[11px] font-bold border border-cyan-100 w-fit"
          >
            <span className="text-rose-600">*</span>
            Alege aroma: {product.variants.map((v) => v.label).join(" · ")}
          </div>
        )}

        <div className="mt-4 flex items-baseline gap-2.5">
          <span className="text-2xl md:text-3xl font-black text-slate-900 font-heading">
            {product.price} lei
          </span>
          {product.oldPrice && (
            <span className="text-sm line-through text-slate-400">
              {product.oldPrice} lei
            </span>
          )}
        </div>

        <div className="mt-5 grid grid-cols-1 gap-2.5">
          <button
            onClick={handleOrderNow}
            data-testid={`add-to-cart-${product.id}`}
            className="h-12 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors shadow-md shadow-red-100"
          >
            <ShoppingCart className="w-4 h-4" />
            Comandă acum
          </button>
          <a
            href={`tel:${PHONE_DISPLAY.replace(/[\s()]/g, "")}`}
            data-testid={`call-now-${product.id}`}
            className="h-12 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors shadow-md shadow-cyan-100"
          >
            <Phone className="w-4 h-4" strokeWidth={2.5} />
            Sună-ne acum: {PHONE_DISPLAY}
          </a>
          <button
            onClick={handleWhatsApp}
            data-testid={`whatsapp-order-${product.id}`}
            className="h-12 rounded-xl bg-[#25d366] hover:bg-[#1da851] text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors shadow-md shadow-emerald-100"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
            </svg>
            Comandă pe WhatsApp
          </button>
          <Link
            to={`/produs/${product.slug}`}
            data-testid={`product-detail-link-${product.id}`}
            className="h-11 rounded-xl bg-white border-2 border-slate-200 hover:border-cyan-500 text-slate-800 font-semibold text-sm flex items-center justify-center gap-1.5 transition-colors"
          >
            Detalii
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </article>
  );
};
